import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../helpers/axios';
import AgentDashboard from '../agent/AgentDashboard';
import AgentLoans from '../agent/AgentLoans';
import AgentIncentive from '../agent/AgentIncentive';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UndoIcon from '@mui/icons-material/Undo';

const AGENT_TABS = ['Dashboard', 'Loans', 'Incentive'];
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

function AgentSwitcher({ agent, agents, onExit, onSelectAgent }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const onOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onOutsideClick, true);
    return () => document.removeEventListener('click', onOutsideClick, true);
  }, []);

  const q = query.trim().toLowerCase();
  const filteredAgents = q
    ? agents.filter((a) => (a.agentName || '').toLowerCase().includes(q))
    : agents;

  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}
    >
      <span style={{ fontFamily: 'Inter-Medium', fontSize: '13.5px', color: '#94a3b8' }}>
        Agent
      </span>

      <span
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{
          width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#fde2e2',
          color: '#d32028', fontSize: '11px', fontWeight: '700', fontFamily: 'Inter-Medium',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {agent.agentName ? agent.agentName.charAt(0).toUpperCase() : '?'}
        </span>
        <span style={{ fontFamily: 'Inter-Medium', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
          {agent.agentName}
        </span>
        <KeyboardArrowDownIcon style={{ fontSize: '18px', color: '#94a3b8' }} />
      </span>

      <span
        onClick={onExit}
        title="Back to team"
        style={{
          width: '34px', height: '34px', borderRadius: '50%', border: '1px solid #e3e6ea',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: '#64748b', flexShrink: 0,
        }}
      >
        <UndoIcon style={{ fontSize: '17px' }} />
      </span>

      {open && (
        <div style={{
          position: 'absolute', top: '38px', right: 0, zIndex: 20, width: '280px',
          backgroundColor: 'white', border: '1px solid #d8dde3', borderRadius: '10px',
          boxShadow: '0 14px 32px rgba(15,23,42,0.18)', padding: '6px',
        }}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agents"
            style={{
              width: '100%', boxSizing: 'border-box', border: '1px solid #d8dde3', borderRadius: '7px',
              padding: '6px 9px', fontFamily: 'Inter-Medium', fontSize: '12.5px', color: '#1f2937',
              outline: 'none', marginBottom: '4px',
            }}
          />
          {filteredAgents.length === 0 ? (
            <div style={{ padding: '8px 8px', fontFamily: 'Inter-Medium', fontSize: '12.5px', color: '#94a3b8' }}>
              No agents match
            </div>
          ) : (
            filteredAgents.map((a) => {
              const isCurrent = a.agentId === agent.agentId;
              return (
                <div
                  key={a.agentId}
                  onClick={() => { onSelectAgent(a); setOpen(false); setQuery(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '5px 6px', borderRadius: '7px', cursor: 'pointer',
                    backgroundColor: isCurrent ? '#fbcaca' : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = '#fde2e2'; }}
                  onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#f1f3f6',
                    color: '#475569', fontSize: '10px', fontWeight: '700', fontFamily: 'Inter-Medium',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {a.agentName ? a.agentName.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{
                      fontFamily: 'Inter-Medium', fontSize: '12.5px', fontWeight: '500', lineHeight: '16px',
                      color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {a.agentName}
                    </div>
                    <div style={{
                      fontFamily: 'Inter-Medium', fontSize: '11px', lineHeight: '14px', color: '#94a3b8',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {fmt(a.recovered)} · {a.loansAssigned ?? 0} loans
                    </div>
                  </div>
                  {isCurrent && (
                    <div style={{
                      flexShrink: 0, whiteSpace: 'nowrap',
                      fontFamily: 'Inter-Medium', fontSize: '9px', fontWeight: '600', letterSpacing: '0.06em',
                      color: '#d32028', backgroundColor: '#fdeced', borderRadius: '999px', padding: '2px 6px',
                    }}>
                      VIEWING
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function AgentPeekView({ agent, agents, onExit, onSelectAgent }) {
  const [activeTab, setActiveTab] = useState(0);

  const renderPeekContent = () => {
    if (activeTab === 0) return <AgentDashboard agentId={agent.agentId} />;
    if (activeTab === 1) return <AgentLoans agentId={agent.agentId} />;
    if (activeTab === 2) return <AgentIncentive agentId={agent.agentId} />;
    return null;
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: 'white', border: '1px solid #d8dde3', borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(15,23,42,0.05)', padding: '0 20px', marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          {AGENT_TABS.map((tab, index) => (
            <button
              key={tab}
              onClick={() => setActiveTab(index)}
              style={{
                padding: '13px 14px 10px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
                fontFamily: 'Inter-Medium', fontSize: '14px',
                fontWeight: activeTab === index ? '600' : '400',
                color: activeTab === index ? '#101828' : '#667085',
                borderBottom: activeTab === index ? '2px solid #d32028' : '2px solid transparent',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <AgentSwitcher agent={agent} agents={agents} onExit={onExit} onSelectAgent={onSelectAgent} />
      </div>

      {renderPeekContent()}
    </div>
  );
}

function mergeAgentsWithSummary(agentsRaw, dashboardRaw) {
  const agentList   = agentsRaw?.agents     || [];
  const teamSummary = dashboardRaw?.teamSummary || [];
  const summaryMap  = {};
  for (const s of teamSummary) summaryMap[s.agentId] = s;
  return agentList.map((a) => ({
    ...a,
    recovered:       summaryMap[a.agentId]?.recovered       ?? 0,
    ptpCount:        summaryMap[a.agentId]?.ptpCount        ?? 0,
    incentiveEarned: summaryMap[a.agentId]?.incentiveEarned ?? 0,
  }));
}

export default function ManagerTeam({ initialData }) {
  const [agents, setAgents]             = useState(initialData ? mergeAgentsWithSummary(initialData.agentsRaw, initialData.dashboardRaw) : []);
  const [loading, setLoading]           = useState(!initialData);
  const [peekingAgent, setPeekingAgent] = useState(null);

  useEffect(() => {
    if (initialData) return;
    Promise.all([
      axiosInstance.get('/collection-incentive/manager/agents'),
      axiosInstance.get('/collection-incentive/manager/dashboard'),
    ])
      .then(([agentsRes, dashRes]) => {
        setAgents(mergeAgentsWithSummary(agentsRes.data.data, dashRes.data.data));
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load agents');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialData) return;
    setAgents(mergeAgentsWithSummary(initialData.agentsRaw, initialData.dashboardRaw));
    setLoading(false);
  }, [initialData]);

  if (loading) return <div style={{ padding: '20px', color: '#667085' }}>Loading...</div>;

  if (peekingAgent) {
    return (
      <AgentPeekView
        agent={peekingAgent}
        agents={agents}
        onExit={() => setPeekingAgent(null)}
        onSelectAgent={setPeekingAgent}
      />
    );
  }

  return (
    <div style={{ padding: '10px' }}>

      {/* ── Agent Cards Grid ── */}
      {agents.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '40px', color: '#667085' }}>No agents found</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          {agents.map((agent) => {
            const isActive = agent.availability === 'Active';
            return (
              <div
                key={agent.agentId}
                onClick={() => setPeekingAgent(agent)}
                style={{
                  width: '280px', backgroundColor: 'white',
                  border: '1px solid #f0f0f0', borderRadius: '10px',
                  overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; }}
              >
                {/* Coloured header band */}
                <div style={{
                  background: isActive
                    ? 'linear-gradient(135deg, #d32028 0%, #ff6b6b 100%)'
                    : 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
                  padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '26px', height: '26px', borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{ color: 'white', fontSize: '11px', fontWeight: '700', fontFamily: 'Inter-Medium' }}>
                        {agent.agentName ? agent.agentName.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: 'white', fontFamily: 'Inter-Medium' }}>
                      {agent.agentName || '—'}
                    </p>
                  </div>
                  <span style={{
                    fontSize: '9px', fontWeight: '700',
                    color: isActive ? '#d32028' : '#757575',
                    backgroundColor: 'white',
                    padding: '2px 6px', borderRadius: '8px', letterSpacing: '0.3px',
                  }}>
                    {isActive ? 'ACTIVE' : 'AWAY'}
                  </span>
                </div>

                {/* Stats body */}
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ display: 'flex', marginBottom: '6px' }}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#101828' }}>
                        {agent.loansAssigned ?? 0}
                      </p>
                      <p style={{ margin: '1px 0 0', fontSize: '9px', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Loans
                      </p>
                    </div>
                    <div style={{ width: '1px', backgroundColor: '#f0f0f0' }} />
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: agent.recovered > 0 ? '#2e7d32' : '#101828' }}>
                        {agent.recovered > 0 ? fmt(agent.recovered) : '—'}
                      </p>
                      <p style={{ margin: '1px 0 0', fontSize: '9px', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Recovery
                      </p>
                    </div>
                    <div style={{ width: '1px', backgroundColor: '#f0f0f0' }} />
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#101828' }}>
                        {agent.ptpCount ?? 0}
                      </p>
                      <p style={{ margin: '1px 0 0', fontSize: '9px', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        PTP
                      </p>
                    </div>
                  </div>
                  {agent.incentiveEarned > 0 && (
                    <div style={{ textAlign: 'center', backgroundColor: '#f0fdf4', borderRadius: '6px', padding: '3px 0' }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#2e7d32' }}>
                        🏆 {fmt(agent.incentiveEarned)} earned
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
